import { useQueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApprovalFlowModal } from '@/accessControl/ApprovalFlowModal.molecule';
import { ApprovalStepModal } from '@/accessControl/ApprovalStepModal';
import { useApprovalFlows } from '@/accessControl/hooks/useApprovalFlows.hooks';
import { useProductionVersionStatus } from '@/accessControl/hooks/useProductionVersionStatus.hooks';
import { useLockAndVersion } from '@/accessControl/useLockAndVersion.hook';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { LoadingSpinner } from '@/main/LoadingSpinner.molecule';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { isApprovalFlowsEnabled } from '@/services/systemConfiguration.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import { useGetWorkbook } from '@/workbook/hooks/useGetWorkbook.hook';
import { normalizeGqlApprovalFlowData } from '@/worksheet/worksheet.utilities';
import { useApiCreateApprovalFlowAssignmentMutation } from '@seeqdev/api/endpoint/ApprovalFlowAssignmentsApi';
import { useApiCreateApprovalFlowAttachmentMutation } from '@seeqdev/api/endpoint/ApprovalFlowAttachmentsApi';
import { GetVersionHistoriesQueryKey } from '@seeqdev/api/endpoint/AuditApi';
import { faChevronDown } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Badge, ButtonWithDropdown, Icon, TriggerWithDropdown } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';
import type { ApprovalFlowAssignmentOutputV1 } from '@seeqdev/api/model/models';

type ActiveModal = null | 'FLOW_MODAL' | 'STEP_MODAL';

/** Displays the approval entry point or current status without owning the workflow. */
const ApprovalFlowHeaderDisplay = ({
  isInProd,
  approved,
  isResolvingProductionStatus,
  showApplyBtn,
  pendingStepName,
  canApprove,
  isFlowModalOpen,
  onApply,
  onApprove,
  onViewSteps,
}: {
  isInProd: boolean;
  approved: boolean | undefined;
  isResolvingProductionStatus: boolean;
  showApplyBtn: boolean;
  pendingStepName: string | undefined;
  canApprove: boolean;
  isFlowModalOpen: boolean;
  onApply: () => void;
  onApprove: () => void;
  onViewSteps: () => void;
}) => {
  const { t } = useTranslation();

  if (isInProd) {
    return <Badge variant="success">{t('APPROVAL_FLOWS.IN_PROD')}</Badge>;
  }
  if (approved) {
    if (isResolvingProductionStatus) {
      return <LoadingSpinner />;
    }
    return <Badge variant="neutral">{t('APPROVAL_FLOWS.DEPRECATED')}</Badge>;
  }
  if (showApplyBtn) {
    return (
      <span className="tw:inline-flex tw:rounded-sm tw:focus-within:ring-2 tw:focus-within:ring-sq-theme-link">
        <TriggerWithDropdown
          triggerIcon={
            <>
              <span className="tw:sr-only">{t('WORKBOOK_ACTIONS')}</span>
              <Icon icon={faChevronDown} type="inherit" />
            </>
          }
          align="start"
          alignOffset={0}
          extraClassNames="tw:cursor-pointer"
          setFocusOnTriggerOnClose={!isFlowModalOpen}
          dropdownItems={[{ label: t('APPROVAL_FLOWS.APPLY_TITLE'), onClick: onApply }]}
        />
      </span>
    );
  }
  return (
    <ButtonWithDropdown
      label={
        <>
          <span>{pendingStepName}</span>
          <Icon icon={faChevronDown} testId="approvalFlowStepChevron" type="inherit" extraClassNames="tw:pl-0.5" />
        </>
      }
      size="xs"
      testId="approvalFlowStep"
      dropdownItems={[
        {
          label: t('APPROVAL_FLOWS.OPTIONS.APPROVE_STEP'),
          // Write on the step is the right to approve into it, which is what createApprovalFlowAssignment enforces.
          disabled: !canApprove,
          onClick: onApprove,
        },
        {
          label: t('APPROVAL_FLOWS.OPTIONS.VIEW_STEPS'),
          onClick: onViewSteps,
        },
      ]}
    />
  );
};

const focusHeaderControl = (control: HTMLSpanElement | null): void => {
  (control?.querySelector('button') ?? control)?.focus();
};

/** Owns approval dialogs, mutations and refreshed workbook status in the header. */
export const ApprovalFlowHeaderControl = () => {
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const controlRef = useRef<HTMLSpanElement>(null);
  const [attachmentFocusRequest, setAttachmentFocusRequest] = useState(0);
  // Wait for the refreshed control to commit; ordinary status refreshes must not move focus.
  useLayoutEffect(() => {
    if (attachmentFocusRequest > 0) {
      focusHeaderControl(controlRef.current);
    }
  }, [attachmentFocusRequest]);
  const { approvalFlow, nextStep, futureStep, showApplyBtn, showStepDropdown, workbookId, approved } =
    useApprovalFlows();
  const { isLocked } = useFluxPaths(sqWorkbookStore, ['isLocked']);
  const { stateParams } = useFluxPaths(sqWorkbenchStore, ['stateParams']);
  const { worksheetId, workstepId } = stateParams;
  const approvalFlowsEnabled = isApprovalFlowsEnabled();
  const isTopic = useWorkbookIsTopic();
  const queryClient = useQueryClient();
  const itemType = isTopic ? t('APPROVAL_FLOWS.ITEM_TYPES.TOPIC') : t('APPROVAL_FLOWS.ITEM_TYPES.ANALYSIS');
  const { inProductionItemIds, isFetching, hasResult } = useProductionVersionStatus([workbookId], {
    enabled: Boolean(approvalFlowsEnabled && approvalFlow && (showApplyBtn || showStepDropdown)),
  });
  const isInProd = inProductionItemIds.has(workbookId);
  const { fetch } = useGetWorkbook({
    workbookId,
    worksheetId,
    workstepId,
    skipWorksheets: headlessRenderMode(),
  });

  const updateWorksheetStore = async () => {
    try {
      const { data } = await fetch();
      const flowData = normalizeGqlApprovalFlowData(data?.workbook);
      flux.dispatch(
        'WORKBOOK_SET_APPROVAL_FLOW',
        {
          approvalFlows: flowData.approvalFlows,
          approvalFlowAssignments: flowData.approvalFlowAssignments,
          attachments: flowData.attachments,
        },
        PUSH_IGNORE,
      );
    } catch (error) {
      errorToast({ httpResponseOrError: error });
    }
  };
  const { mutate: createApprovalFlowAttachment, isPending: isAttaching } = useApiCreateApprovalFlowAttachmentMutation({
    onSuccess: async () => {
      successToast({ messageKey: 'APPROVAL_FLOWS.APPLY_SUCCESS' });
      setActiveModal(null);
      await updateWorksheetStore();
      setAttachmentFocusRequest((request) => request + 1);
    },
    onError: (error) => {
      errorToast({ httpResponseOrError: error });
    },
  });

  const {
    mutate: createAssignment,
    mutateAsync: createAssignmentPromise,
    isPending: isAssigning,
  } = useApiCreateApprovalFlowAssignmentMutation({
    onSuccess: async () => {
      successToast({ messageKey: 'APPROVAL_FLOWS.PROMOTE_SUCCESS' });
      setActiveModal(null);
      await updateWorksheetStore();
      await queryClient.invalidateQueries({
        queryKey: GetVersionHistoriesQueryKey({}).slice(0, 2),
      });
    },
    onError: (error) => {
      errorToast({ httpResponseOrError: error });
    },
  });

  const { lockAndVersion, hasLockingFailedPreviouslyDueToGlobalItems } =
    useLockAndVersion<ApprovalFlowAssignmentOutputV1>({
      itemId: workbookId,
      onBeforeRedirect: () => {
        if (!approvalFlow || !nextStep) {
          return Promise.resolve({} as ApprovalFlowAssignmentOutputV1);
        }
        return createAssignmentPromise([{ stepId: nextStep?.id, itemId: workbookId }, { flowId: approvalFlow?.id }]);
      },
      closeModal: () => setActiveModal(null),
    });

  const applyApprovalFlow = async () => {
    approvalFlow && createApprovalFlowAttachment([{ itemId: workbookId }, { flowId: approvalFlow?.id }]);
  };

  const approveStep = async (step: NonNullable<typeof nextStep>, versionState?: VersionState) => {
    const needsLocking = step.lockRequired && !isLocked;
    if (needsLocking && versionState) {
      try {
        return await lockAndVersion(versionState);
      } catch (error) {
        return errorToast({ httpResponseOrError: error });
      }
    }
    return approvalFlow && createAssignment([{ stepId: step.id, itemId: workbookId }, { flowId: approvalFlow?.id }]);
  };

  const isResolvingProductionStatus = isAssigning || (isFetching && !hasResult);

  if (!approvalFlowsEnabled || (!showApplyBtn && !showStepDropdown)) {
    return null;
  }

  return (
    <>
      <span
        ref={controlRef}
        tabIndex={-1}
        className={`tw:col-start-2 tw:ml-sq-9 tw:flex tw:items-center ${
          showApplyBtn && !isInProd && !approved ? 'tw:row-start-2' : 'tw:row-span-2 tw:row-start-1'
        }`}>
        <ApprovalFlowHeaderDisplay
          isInProd={isInProd}
          approved={approved}
          isResolvingProductionStatus={isResolvingProductionStatus}
          showApplyBtn={showApplyBtn}
          pendingStepName={nextStep?.name}
          canApprove={Boolean(nextStep?.permissions.write)}
          isFlowModalOpen={activeModal === 'FLOW_MODAL'}
          onApply={() => setActiveModal('FLOW_MODAL')}
          onApprove={() => setActiveModal('STEP_MODAL')}
          onViewSteps={() => setActiveModal('FLOW_MODAL')}
        />
      </span>
      {activeModal === 'FLOW_MODAL' ? (
        <ApprovalFlowModal
          isApplyingFlow={showApplyBtn}
          approvalFlow={approvalFlow}
          itemType={itemType}
          isApplying={isAttaching}
          onCloseAutoFocus={
            showApplyBtn
              ? (event) => {
                  event.preventDefault();
                  focusHeaderControl(controlRef.current);
                }
              : undefined
          }
          onSubmit={showApplyBtn ? applyApprovalFlow : async () => setActiveModal(null)}
          onClose={() => setActiveModal(null)}
        />
      ) : null}
      {activeModal === 'STEP_MODAL' && nextStep ? (
        <ApprovalStepModal
          step={nextStep}
          futureStep={futureStep}
          approvalFlow={approvalFlow}
          itemType={itemType}
          isSubmitting={isAssigning}
          hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems}
          onSubmit={async (versionState) => {
            await approveStep(nextStep, versionState);
          }}
          onClose={() => setActiveModal(null)}
          isItemLocked={isLocked}
        />
      ) : null}
    </>
  );
};
